import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import FileUpload from '../components/FileUpload';
import {
  FileText, Image as ImageIcon, File, Download, Trash2, 
  Search, Filter, Grid, List, Eye, Upload
} from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FileManager = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'images', 'documents'
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/files`, {
        headers: getAuthHeaders()
      });
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Failed to fetch files:', error);
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUploaded = (file) => {
    fetchFiles();
    setShowUpload(false);
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await axios.delete(`${API}/files/${fileId}`, {
        headers: getAuthHeaders()
      });
      toast.success('File deleted');
      fetchFiles();
    } catch (error) {
      console.error('Failed to delete file:', error);
      toast.error('Failed to delete file');
    }
  };

  const handleDownload = (file) => {
    window.open(`${API}/files/${file.file_id}`, '_blank');
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <ImageIcon className="w-6 h-6" />;
    if (type === 'application/pdf') return <FileText className="w-6 h-6" />;
    return <File className="w-6 h-6" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredFiles = files.filter(file => {
    // Search filter
    const matchesSearch = file.filename?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Type filter
    let matchesType = true;
    if (filterType === 'images') {
      matchesType = file.mime_type?.startsWith('image/');
    } else if (filterType === 'documents') {
      matchesType = !file.mime_type?.startsWith('image/');
    }
    
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="text-muted-foreground mt-4">Loading files...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="file-manager-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-5xl font-bold text-white mb-3">FILE MANAGER</h1>
            <p className="text-muted-foreground text-lg">
              {filteredFiles.length} file{filteredFiles.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={() => setShowUpload(true)}
            data-testid="upload-button"
            className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold px-8 py-4 flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Upload Files
          </button>
        </div>

        {/* Filters */}
        <div className="glass-card p-6 border border-white/10">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-secondary/50 border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none text-white"
              />
            </div>

            {/* Type Filter */}
            <div className="flex gap-2">
              <button
                onClick={() => setFilterType('all')}
                className={`px-4 py-2 text-xs uppercase tracking-widest ${
                  filterType === 'all'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary/50 text-muted-foreground hover:text-white'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilterType('images')}
                className={`px-4 py-2 text-xs uppercase tracking-widest ${
                  filterType === 'images'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary/50 text-muted-foreground hover:text-white'
                }`}
              >
                Images
              </button>
              <button
                onClick={() => setFilterType('documents')}
                className={`px-4 py-2 text-xs uppercase tracking-widest ${
                  filterType === 'documents'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary/50 text-muted-foreground hover:text-white'
                }`}
              >
                Documents
              </button>
            </div>

            {/* View Toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${
                  viewMode === 'grid'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary/50 text-muted-foreground hover:text-white'
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${
                  viewMode === 'list'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary/50 text-muted-foreground hover:text-white'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Upload Modal */}
        {showUpload && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 border border-white/10 max-w-2xl w-full">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Upload Files</h2>
                <button
                  onClick={() => setShowUpload(false)}
                  className="text-muted-foreground hover:text-white"
                >
                  ×
                </button>
              </div>
              <FileUpload onFileUploaded={handleFileUploaded} />
            </div>
          </div>
        )}

        {/* Files Display */}
        {filteredFiles.length === 0 ? (
          <div className="text-center py-20">
            <File className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground text-lg">
              {searchQuery || filterType !== 'all'
                ? 'No files match your filters'
                : 'No files uploaded yet'}
            </p>
            <button
              onClick={() => setShowUpload(true)}
              className="mt-4 text-primary hover:text-primary/80"
            >
              Upload your first file
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredFiles.map((file) => (
              <div
                key={file.file_id}
                className="glass-card p-4 border border-white/10 hover:border-primary/50 transition-all group"
              >
                {/* File Icon/Preview */}
                <div className="aspect-square bg-secondary/30 rounded-lg flex items-center justify-center mb-3 relative overflow-hidden">
                  {file.mime_type?.startsWith('image/') ? (
                    <img
                      src={`${API}/files/${file.file_id}`}
                      alt={file.filename}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-primary">
                      {getFileIcon(file.mime_type)}
                    </div>
                  )}
                  
                  {/* Actions Overlay */}
                  <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => handleDownload(file)}
                      className="p-2 bg-primary hover:bg-primary/90 rounded"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(file.file_id)}
                      className="p-2 bg-destructive hover:bg-destructive/90 rounded"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* File Info */}
                <div className="space-y-1">
                  <p className="text-sm font-medium text-white truncate" title={file.filename}>
                    {file.filename}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(file.size)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(file.uploaded_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="glass-card border border-white/10 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-widest text-muted-foreground">
                    Name
                  </th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-widest text-muted-foreground">
                    Type
                  </th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-widest text-muted-foreground">
                    Size
                  </th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-widest text-muted-foreground">
                    Date
                  </th>
                  <th className="text-right px-6 py-4 text-xs uppercase tracking-widest text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map((file) => (
                  <tr
                    key={file.file_id}
                    className="border-b border-white/5 hover:bg-secondary/30 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="text-primary">
                          {getFileIcon(file.mime_type)}
                        </div>
                        <span className="text-sm text-white truncate max-w-xs">
                          {file.filename}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {file.mime_type?.split('/')[1]?.toUpperCase()}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {formatFileSize(file.size)}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {formatDate(file.uploaded_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleDownload(file)}
                          className="p-2 hover:bg-primary/20 rounded text-primary"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(file.file_id)}
                          className="p-2 hover:bg-destructive/20 rounded text-destructive"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FileManager;
